import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import Input from "../../../components/Input";
import Button from "../../../components/Button";
import Select from "../../../components/Select";
import messages from "../../../assets/locale/messages";
import { getCategoriesRequest } from "../../../store/Categories/actions";
import {
  addSpecialtyRequest,
  editSpecialtyRequest,
  getSpecialtyDetailsRequest,
  getSpecialtyDetailsResponse,
} from "../../../store/Specialties/actions";
import {
  ARABIC_REGEX,
  ENGLISH_REGEX,
  NAME_REGEX,
  ONE_SPACE_ONLY,
  ENGLISH_LOOKUP_NAME_REGEX,
  ARABIC_LOOKUP_NAME_REGEX,
  ATLEAST_TWO_ENGLISH_CHARACTERS,
  ATLEAST_TWO_ARABIC_CHARACTERS,
} from "../../../utils/Patterns";
import { setPageMainTitle } from "../../../utils/Helpers";
import {ROUTE_PATHS} from "../../../utils/RoutesPaths";

const AddEditSpecialties = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const categoriesList = useSelector((state) => state.categories.list);
  const specialtyDetails = useSelector(
    (state) => state.specialties.specialtyDetails
  );
  const lang = useSelector((state) => state.locale.lang);
  const { shared, specialties, pagesTitles } = messages[lang];

  const [categoriesOptions, setCategoriesOptions] = useState([]);

  useEffect(() => {
    setPageMainTitle("specialtiesManagement");
    dispatch(getCategoriesRequest({ page: -1 }));
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(getSpecialtyDetailsRequest(id));
    }
    return () => {
      dispatch(getSpecialtyDetailsResponse({}));
    };
  }, [id]);

  useEffect(() => {
    if (id && specialtyDetails) {
      setFieldTouched({
        title_en: true,
        title_ar: true,
        category_ids: true,
      });
      setValues({
        title_en: specialtyDetails.title_en,
        title_ar: specialtyDetails.title_ar,
        category_ids: specialtyDetails.categories?.map(
          (category) => category.id
        ),
      });
    }
  }, [specialtyDetails, id]);

  useEffect(() => {
    setCategoriesOptions(
      categoriesList?.map((category) => ({
        id: category.id,
        label: lang === "en" ? category.title_en : category.title_ar,
      }))
    );
  }, [categoriesList, lang]);

  const {
    values,
    setFieldValue,
    setValues,
    handleSubmit,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: {
      title_en: "",
      title_ar: "",
      category_ids: [],
    },
    validationSchema: Yup.object({
      title_en: Yup.string()
        .required("enNameRequired")
        // .matches(NAME_REGEX, "nameNotFollowREGEX")
        // .matches(ENGLISH_REGEX, "englishLettersOnly"),
        .matches(ENGLISH_LOOKUP_NAME_REGEX, "englishLettersAndSpecialCharactersOnly")
        .matches(ATLEAST_TWO_ENGLISH_CHARACTERS, "atleastTwoEnglishCharacters"),
      title_ar: Yup.string()
        .required("arNameRequired")
        // .matches(NAME_REGEX, "nameNotFollowREGEX")
        // .matches(ARABIC_REGEX, "arabicLettersOnly"),
        .matches(ARABIC_LOOKUP_NAME_REGEX, "arabicLettersAndSpecialCharactersOnly")
        .matches(ATLEAST_TWO_ARABIC_CHARACTERS, "atleastTwoArabicCharacters"),
      category_ids: Yup.array().min(1, "pickOneCategoryAtLeast"),
    }),
    onSubmit: ({ title_en, title_ar, category_ids }) => {
      id
        ? dispatch(
            editSpecialtyRequest({
              id: +id,
              data: {
                title_en: title_en.trim(),
                title_ar: title_ar.trim(),
                category_ids,
              },
            })
          )
        : dispatch(
            addSpecialtyRequest(
              {
                title_en: title_en.trim(),
                title_ar: title_ar.trim(),
                category_ids,
              },
              ()=> history.push(ROUTE_PATHS.specialties)
            )
          );
    },
  });

  const getCategoriesValues = () =>
    values["category_ids"]?.map((categoryId) =>
      categoriesOptions?.find((category) => category.id === categoryId)
    );

  return (
    <div className="add-edit-specialty-page">
      <div className="d-flex mb-4">
        <Typography
          variant="h4"
          sx={{ flexGrow: 1 }}
          className={`page-title ${lang === "en" ? "me-5" : "ms-5"}`}
        >
          {id ? pagesTitles.specialtiesEdit : pagesTitles.specialtiesAdd}
        </Typography>
      </div>
      <form noValidate onSubmit={handleSubmit}>
        <div className="px-2 px-md-5 bg-white rounded-4">
          <div className="pt-4">
            <div className="row mt-4">
              <div className="col-12 col-md-6">
                <Input
                  id="title_en"
                  label={shared.lists.enTitle}
                  name="title_en"
                  type="text"
                  placeholder={specialties.addSpecialty.placeholders.enTitle}
                  required
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("title_en");
                      setFieldValue("title_en", value);
                    }
                  }}
                  value={values["title_en"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["title_en"] && errors["title_en"])}
                  errMsg={errors["title_en"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Input
                  id="title_ar"
                  label={shared.lists.arTitle}
                  name="title_ar"
                  type="text"
                  placeholder={specialties.addSpecialty.placeholders.arTitle}
                  required
                  onChange={(value) => {
                    if (ONE_SPACE_ONLY.test(value) || !value) {
                      setFieldTouched("title_ar");
                      setFieldValue("title_ar", value);
                    }
                  }}
                  value={values["title_ar"]}
                  inputWrapperClass="mb-4"
                  labelClassName="text-dark font-medium mb-2"
                  isInputHasErr={!!(touched["title_ar"] && errors["title_ar"])}
                  errMsg={errors["title_ar"]}
                />
              </div>
              <div className="col-12 col-md-6">
                <Select
                  labelClassName="text-dark font-medium mb-2"
                  label={specialties.addSpecialty.labels.category}
                  name="category_ids"
                  id="category_ids"
                  options={categoriesOptions}
                  value={getCategoriesValues() || []}
                  placeholder={specialties.addSpecialty.placeholders.category}
                  required
                  onChange={(value) => {
                    setFieldTouched("category_ids");
                    setFieldValue(
                      "category_ids",
                      value.map((category) => category.id)
                    );
                  }}
                  hasError={
                    !!(touched["category_ids"] && errors["category_ids"])
                  }
                  errMsg={errors["category_ids"]}
                  isMultipleSelect={true}
                />
              </div>
            </div>
            <div className="row mt-4 py-4 d-flex justify-content-center">
              <div className="col-4">
                <Button
                  onClick={() => {}}
                  type="submit"
                  className="btn-secondary"
                  block
                  label={shared.save}
                  labelClass="px-4 fsize-16 font-medium text-white"
                  disabled={!dirty || !isValid}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEditSpecialties;
